// BYO-01 company subscription paths: which ways people may bring keys and tools in this company
// (the company flags in `settings.flags`, saved through `PATCH /v1/companies/{co}` with the other
// settings kept). A path the founder has not opened on the platform cannot be turned on here; its
// switch is disabled and says so. What a switch means sits behind its (i).
// CONNECTIONS-AREA (2026-10-30): Setup › Keys & connections moved to Team › Connections; its old
// URL (`/setup/connections?tab=`) opens the matching Connections tab (subscriptions-routes.tsx),
// and these switches are the Connections page's "Paths" modal.
import { InfoTip, SettingsGroup, SettingsRow, Switch, toast } from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { companyQuery, keys } from "../queries";
import { problemText } from "../settings/problem-text";
import { COPY } from "./copy";
import { type Policy, subscriptionKeys } from "./queries";

type FlagKey = keyof Policy["company"];
/** `SUBSCRIPTION_FLAGS` of @companyos/schema (connection-rows.ts); a parity test pins them. */
export const FLAG_OF: Record<FlagKey, string> = {
  model_api_keys: "byo_model_provider_keys",
  model_cli_subscriptions: "byo_model_cli_subscriptions",
  tool_connections: "byo_tool_connections",
  crew_mail_calendar: "crew_mail_calendar",
};

const FLAG_LABEL: Record<FlagKey, string> = {
  model_api_keys: "AI API keys",
  model_cli_subscriptions: "Own CLI logins",
  tool_connections: "Tool connections",
  crew_mail_calendar: "Crew mail and calendar",
};

/** The old Setup › Keys & connections tabs (`?tab=`), each opening its Connections tab. */
export const COMPANY_CONNECTION_TABS = [
  "keys",
  "catalog",
  "connections",
  "access",
  "checks",
] as const;
export type CompanyConnectionTab = (typeof COMPANY_CONNECTION_TABS)[number];

function FlagRow({
  label,
  info,
  status,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  /** What the switch means, behind an (i). */
  info: string;
  /** Why it cannot be turned on now, shown under the label. */
  status?: string;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <SettingsRow
      label={
        <span className="inline-flex items-center gap-1">
          {label}
          <InfoTip label={`About ${label}`}>{info}</InfoTip>
        </span>
      }
      {...(status ? { hint: status } : {})}
    >
      <Switch aria-label={label} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </SettingsRow>
  );
}

/**
 * The company's subscription paths as switch rows (each saves at once with a toast): the AI key
 * paths and the tool paths. Owners and admins.
 */
export function PathSwitches({
  api,
  company,
  policy,
}: {
  api: Api;
  company: CompanyAccess;
  policy: Policy;
}) {
  const qc = useQueryClient();
  const setFlag = useMutation({
    mutationFn: async (x: { key: FlagKey; on: boolean }) => {
      // Fresh: the PATCH replaces the whole settings object (the model policy is in it too).
      const d = await qc.fetchQuery({ ...companyQuery(api, company.id), staleTime: 0 });
      return api.client.companies.update({
        params: { co: company.id },
        body: {
          settings: {
            ...d.settings,
            flags: { ...(d.settings.flags ?? {}), [FLAG_OF[x.key]]: x.on },
          },
        },
      });
    },
    onSuccess: (d, x) => {
      qc.setQueryData(keys.company(company.id), d);
      void qc.invalidateQueries({ queryKey: subscriptionKeys.all(company.id) });
      toast.add({ title: `${FLAG_LABEL[x.key]} ${x.on ? "on" : "off"}`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  const p = policy.platform;
  const flag = (key: FlagKey, info: string, platformOn: boolean) => (
    <FlagRow
      label={FLAG_LABEL[key]}
      info={info}
      {...(platformOn ? {} : { status: "Not opened on the platform yet." })}
      checked={policy.company[key]}
      disabled={!platformOn || setFlag.isPending}
      onChange={(on) => setFlag.mutate({ key, on })}
    />
  );
  return (
    <>
      <SettingsGroup id="paths-keys" title="AI keys">
        {flag(
          "model_api_keys",
          "People add their own provider API keys; admins add company keys.",
          p.person_api_keys || p.company_api_keys,
        )}
        {flag(
          "model_cli_subscriptions",
          "People record their own logged-in Claude Code CLI. No token is stored.",
          p.person_cli_subscriptions,
        )}
      </SettingsGroup>
      <SettingsGroup id="paths-tools" title="Tools">
        {flag("tool_connections", "People and admins connect tools their agents may call.", true)}
        {/* M4-04: needs tool connections on (the API checks both flags). */}
        <FlagRow
          label={FLAG_LABEL.crew_mail_calendar}
          info={COPY.crewMailCalendar}
          {...(policy.company.tool_connections
            ? {}
            : { status: "Turn tool connections on first." })}
          checked={policy.company.crew_mail_calendar}
          disabled={!policy.company.tool_connections || setFlag.isPending}
          onChange={(on) => setFlag.mutate({ key: "crew_mail_calendar", on })}
        />
      </SettingsGroup>
    </>
  );
}
